/**
 * Quality presets and tuning constants.
 *
 * Everything that defines *feel* lives here rather than being scattered through
 * the systems that consume it. Tuning a game is an iterative, empirical process;
 * having every number that matters in one readable file is the difference
 * between a ten-second tweak and a twenty-minute search.
 */

export const QUALITY = {
  low: {
    shadowMapSize: 1024, cascades: 2, shadowDistance: 70,
    bloom: true, bloomMips: 4, godrays: false, fxaa: true,
    particleBudget: 600, decalBudget: 96, pixelRatioCap: 1.0,
    anisotropy: 2, iblSize: 128,
  },
  medium: {
    shadowMapSize: 2048, cascades: 3, shadowDistance: 110,
    bloom: true, bloomMips: 5, godrays: true, fxaa: true,
    particleBudget: 1600, decalBudget: 192, pixelRatioCap: 1.5,
    anisotropy: 4, iblSize: 256,
  },
  high: {
    shadowMapSize: 2048, cascades: 3, shadowDistance: 150,
    bloom: true, bloomMips: 6, godrays: true, fxaa: true,
    particleBudget: 3000, decalBudget: 320, pixelRatioCap: 2.0,
    anisotropy: 8, iblSize: 256,
  },
};

/**
 * Player movement.
 *
 * These numbers are the single most important thing in the project. The defining
 * trait of this movement model is AIR_CONTROL at 0.55 — most shooters sit near
 * 0.1, which makes a jump a commitment. At 0.55 the player retains real
 * authority in the air, and traversal becomes an expressive act rather than an
 * interruption to walking. Everything else here is tuned around that.
 */
export const MOVE = {
  walkSpeed: 5.5,
  sprintSpeed: 7.4,
  crouchSpeed: 2.9,
  groundAccel: 60,
  groundFriction: 11,
  airAccel: 60 * 0.55,      // the signature number
  airDrag: 0.6,
  maxAirSpeed: 9.5,

  // Jump heights follow h = v^2 / 2g. The original 7.2 / 5.8 against g=22 gave
  // a 1.18 m single and a 1.87 m double — far too low to read as floaty, and
  // low enough that the gantry climb was literally impossible to complete.
  // These values give ~1.85 m single and ~3.2 m total on a double, which clears
  // the 2.0 m platform spacing with real margin and feels airborne rather than
  // merely bouncy.
  gravity: 20,
  jumpVelocity: 8.6,
  doubleJumpVelocity: 7.4,
  // A double jump redirects existing horizontal momentum toward the input rather
  // than simply adding to it. This is what lets a player reverse direction
  // mid-air, which is the move that makes the traversal read as "floaty" rather
  // than merely "high".
  doubleJumpRedirect: 0.72,
  maxJumps: 2,

  // Forgiveness windows. Both are essential and neither is cheating: without
  // them, players who feel they pressed jump on the ledge are told otherwise.
  coyoteTime: 0.12,
  jumpBufferTime: 0.15,

  slideEnterSpeed: 6.0,      // minimum speed to enter a slide
  slideDuration: 0.9,
  slideBoost: 1.15,          // multiplier applied to entry speed
  slideFriction: 3.4,
  slideMinSpeed: 3.2,        // drop out of slide below this
  slideCooldown: 0.35,
  slideSteer: 3.2,           // how hard you can carve mid-slide

  standHeight: 1.75,
  crouchHeight: 1.05,
  eyeOffset: -0.18,          // eye sits below the capsule top
  radius: 0.38,
  stepHeight: 0.42,
  maxSlopeDeg: 52,

  fallDamageThreshold: 12,   // m/s impact below which falls are free
  fallDamageScale: 7,
  terminalVelocity: 55,
};

/** Camera feel. Subtle, but this is where "juice" actually lives. */
export const CAMERA = {
  fov: 95,
  sprintFovAdd: 6,
  adsFovMul: 0.72,          // ~ -12 degrees at base fov
  fovLerp: 7.5,

  pitchMin: -Math.PI / 2 + 0.02,
  pitchMax: Math.PI / 2 - 0.02,

  bobFreq: 9.2,
  bobAmp: 0.035,
  bobRunMul: 1.55,

  landDipScale: 0.055,      // metres of dip per m/s of impact
  landDipMax: 0.34,
  landDipRecover: 9,

  strafeRoll: 0.028,        // radians of roll at full strafe
  slideRoll: 0.075,
  rollLerp: 6.5,

  slideHeightDrop: 0.55,
  heightLerp: 13,
};

/** The Ironwake hand cannon. */
export const WEAPON = {
  name: 'Ironwake',
  rpm: 140,
  magSize: 9,
  reserveMax: 90,
  reserveStart: 54,

  bodyDamage: 33,
  // The target TTK is 3 precision hits or 5 body shots against a 165 hp Dreg.
  // That constrains the multiplier tightly: two crits must NOT kill (2m < 5)
  // while three must (3m >= 5), so m lies in [1.67, 2.5). At exactly 2.5 two
  // crits deal 165 — precisely lethal — and the weapon becomes a two-shot,
  // which collapses the whole risk/reward of aiming for heads under fire.
  // 2.4 keeps the punchy multiplier and lands the intended breakpoints:
  // 2 crits = 158 (survives), 3 crits = 238 (kills).
  critMultiplier: 2.4,
  falloffStart: 28,
  falloffEnd: 45,
  falloffFloor: 0.55,       // damage retained at max range

  adsTime: 0.22,
  adsFovMul: 0.72,
  hipSpread: 0.019,         // radians
  adsSpread: 0.0016,
  moveSpreadAdd: 0.014,
  airSpreadAdd: 0.020,

  reloadTime: 1.9,
  reloadEjectAt: 0.30,      // normalized time the cylinder swings out
  reloadInsertAt: 0.62,
  reloadCloseAt: 0.86,

  recoilPitch: 0.052,       // radians per shot
  recoilYaw: 0.011,         // gaussian-scattered
  recoilKickback: 0.055,    // metres the viewmodel travels back
  recoilRecover: 0.35,      // seconds to settle
  recoilRecoverFrac: 0.72,  // fraction of pitch that returns automatically

  range: 220,
  tracerSpeed: 480,
};

/** Player vitals. Two-layer shield over health, both fully recoverable. */
export const VITALS = {
  shieldMax: 100,
  healthMax: 100,
  shieldRegenDelay: 4.0,
  shieldRegenRate: 60,
  healthRegenDelay: 9.0,
  healthRegenRate: 15,
  lowHealthFrac: 0.35,
};

/** Arc abilities. */
export const ABILITIES = {
  grenadeCooldown: 45,
  grenadeThrowSpeed: 21,
  grenadeStickRadius: 0.35,
  grenadePulses: 4,
  grenadePulseInterval: 0.75,
  grenadePulseDamage: 60,
  grenadePulseRadius: 4.6,
  grenadeChainRadius: 4.0,
  grenadeFuse: 0.55,

  meleeCooldown: 25,
  meleeChargedDamage: 180,
  meleeLightDamage: 45,
  meleeRange: 3.0,
  meleeDashRange: 5.0,
  meleeDashSpeed: 26,
  meleeDashTime: 0.19,

  superDuration: 12,
  superSpeedMul: 1.4,
  superDamageResist: 0.55,   // fraction of incoming damage removed
  superComboDamage: [250, 250, 400],
  superComboWindow: 0.55,
  superSwingTime: 0.34,
  superRange: 4.2,
  // Meter economy, tuned against the level's actual enemy count rather than in
  // the abstract. The first arena is 8 Dregs and the gantry ambush is 3, so
  // charging at ~11 basic kills means the super comes online exactly as the
  // player reaches the final arena — which is where the level is built to spend
  // it. The original rates charged it in ~9 kills, handing the player their
  // super midway through the first fight where it trivialises the encounter.
  superFromDamageDealt: 1 / 26,
  superFromDamageTaken: 1 / 18,
  superFromKill: 2.8,
  superFromAbilityKill: 5,
  superMax: 100,
  superStartFrac: 0,
};

/**
 * Cabal Legionary.
 *
 * Health stays at 165 deliberately. The 3-precision / 5-body time-to-kill is a
 * verified breakpoint the whole weapon design rests on, and raising health to
 * make Cabal "feel tankier" would either break it or push body-shot kills to
 * seven rounds — three quarters of a magazine for one enemy.
 *
 * Cabal read as heavier through BEHAVIOUR instead: they advance rather than
 * strafe, hold ground under fire, resist stagger, hit harder per shot, and
 * shoulder-charge anyone who lets them close. The silhouette is ~1.3x a human
 * and moves slowly, so they are threatening to be near rather than hard to kill.
 */
export const CABAL = {
  health: 165,              // 3 crits (238) or 5 body (165) exactly
  critMultiplier: 2.4,
  scale: 1.3,
  moveSpeed: 3.4,           // slower and heavier than a skirmisher
  sprintSpeed: 5.2,
  sightRange: 65,
  sightHalfAngleDeg: 55,
  hearingRange: 34,
  // Projectiles, not hitscan. Slow enough to read and sidestep — enemies
  // telegraph, the player jukes. This is the core of the combat conversation.
  // Cabal slugs are marginally faster and hit harder than a skirmisher's bolts.
  boltSpeed: 62,
  boltDamage: 17,
  burstCount: 3,
  burstInterval: 0.14,
  fireCooldownMin: 1.2,
  fireCooldownMax: 2.3,
  // They prefer to close: a Cabal line walks toward you, which is what makes
  // them feel like an advancing army rather than a set of turrets.
  preferredRangeMin: 10,
  preferredRangeMax: 20,
  coverHealthFrac: 0.28,    // holds ground far longer before breaking for cover
  meleeRange: 7,
  meleeDamage: 34,
  meleeCooldown: 3.2,
  reactionTimeMin: 0.28,
  reactionTimeMax: 0.55,    // heavier, slower to react
  accuracy: 0.74,
  flinchDuration: 0.12,     // armour absorbs; less visible hitch than a Dreg
  staggerThreshold: 150,    // takes a genuinely heavy hit to interrupt
  // Legionary shoulder charge — the reason you cannot simply back away.
  chargeSpeed: 11,
  chargeDamage: 38,
  chargeCooldown: 11,
};

/** Cabal Centurion — the shielded elite. */
export const ELITE = {
  health: 800,
  shield: 300,
  shieldRegenDelay: 6,
  shieldRegenRate: 40,
  moveSpeed: 3.0,
  sprintSpeed: 4.2,
  sightRange: 75,
  sightHalfAngleDeg: 60,
  boltSpeed: 68,
  boltDamage: 28,
  burstCount: 4,
  burstInterval: 0.15,
  fireCooldownMin: 1.4,
  fireCooldownMax: 2.4,
  preferredRangeMin: 8,
  preferredRangeMax: 18,
  meleeRange: 8,
  chargeSpeed: 14,
  chargeDamage: 58,
  chargeCooldown: 8,
  scale: 1.75,
};

export const AMMO = {
  brickChance: 0.35,
  brickRounds: 12,
  pickupRadius: 1.6,
  brickLifetime: 30,
};

/**
 * Palette — Mars: a sand-buried city under a red dust sky.
 *
 * This is deliberately RED, not the bleached ochre it started as. Iron oxide is
 * the whole identity of the planet, and a pale sepia read — while defensible
 * from the reference stills — loses the one thing that says "Mars" at a glance.
 *
 * The risk pushing red is that everything collapses into a single hot mud with
 * no depth and no legibility, so it is managed rather than avoided:
 *
 *   - HUE varies even where saturation is high. Sand runs orange-red, the deep
 *     shadows run toward violet-brown, the sky runs rust. A single hue at three
 *     values reads as a filter; three hues reads as a place.
 *   - VALUE separation is preserved. Sunlit sand stays genuinely bright and
 *     shadowed geometry genuinely dark, so form still reads.
 *   - Two COOL exceptions survive: arc blue for the player's abilities, and the
 *     rim light. Against a fully red world those are the only cold things on
 *     screen, so they carry all the legibility — the player's own kit and the
 *     silhouette edges of everything they need to shoot.
 */
export const PALETTE = {
  arc: 0x69c9ff,
  arcBright: 0xd6f2ff,
  arcDeep: 0x1d5f9e,

  // Ground. Pale gold, not red — this is wind-sorted fine sand over the ruins.
  sand: 0xc06636,
  sandLit: 0xe89052,        // crest of a dune facing the sun
  sandShadow: 0x6b3520,
  grit: 0x9c5530,           // coarser exposed material

  // Architecture. Sun-bleached concrete, dust-stained toward the base.
  concrete: 0xb07048,
  concreteShadow: 0x6e3f28,
  concreteStain: 0x7a3f22,
  steel: 0x836a5c,
  steelDark: 0x42332a,
  rust: 0x9c6238,
  rustDark: 0x5a3520,
  glassTint: 0x53382c,      // dead ribbon windows read dark, not reflective

  cabalPlate: 0x584a3e,     // dusty gunmetal
  cabalTrim: 0xc09030,      // hazard amber
  cabalVisor: 0xff6a2a,     // hot orange visor — one of only two saturated hues

  // Sky. Pale, hazy, low contrast. The horizon is nearly the same value as the
  // ground, which is what makes the distance dissolve into dust.
  skyZenith: 0x7e3520,      // deep rust overhead, darker than the horizon
  skyHorizon: 0xc97043,     // hot dust at the horizon
  sunHalo: 0xffa868,        // warm and diffuse — the sun is a smear, not a disc
  sunColor: 0xffd9a8,
  fogColor: 0xa85434,       // red dust swallows the distance

  critOrange: 0xfff04a,     // near-yellow: the only way a crit reads on red
  damageWhite: 0xfdf8f0,
};

export function makeConfig(overrides = {}) {
  const params = new URLSearchParams(globalThis.location?.search ?? '');
  const qName = overrides.quality ?? params.get('q') ?? 'high';
  const q = QUALITY[qName] ?? QUALITY.high;

  return {
    qualityName: qName,
    q,
    dev: params.has('dev'),
    seed: Number(params.get('seed') ?? 0x5eed1e) >>> 0,
    // Capture mode disables the veil and pointer-lock gate so the screenshot
    // harness can drive the game headlessly.
    capture: params.has('capture'),
    startAt: params.get('at') ?? null, // jump to a named encounter for testing
    muted: params.has('mute'),
    MOVE, CAMERA, WEAPON, VITALS, ABILITIES, CABAL, ELITE, AMMO, PALETTE,
    ...overrides,
  };
}
